import type { ReadWriteApi } from 'v5/platform/ts/readWriteApi';
import {
  resourceTypeBindingsUtils,
  type ResourceTypeBindingsApi,
  type ResourceTypeWorkspaceBinding,
  type SetResourceTypeEnabledInput,
  type SetResourceTypeWorkspaceFieldsInput
} from './resource-type-bindings.api';
import type { ResourceTypeBindingsStore } from './resource-type-bindings.api.ls.schema';

/**
 * In-memory / localStorage-backed {@link ResourceTypeBindingsApi}.
 * Pass {@link import('v5/platform/ts/localStorageApi').createLocalStorageApi}
 * from connected, or {@link import('v5/platform/ts/inMemoryApi').createInMemoryApi}
 * from Storybook / tests.
 */
export function createResourceTypeBindingsApi(
  store: ReadWriteApi<ResourceTypeBindingsStore>
): ResourceTypeBindingsApi {
  /** Serialize read-modify-write so parallel setEnabled/setFields don't clobber. */
  let writeChain: Promise<unknown> = Promise.resolve();

  function enqueueWrite<T>(fn: () => Promise<T>): Promise<T> {
    const next = writeChain.then(fn, fn);
    writeChain = next.then(
      () => undefined,
      () => undefined
    );
    return next;
  }

  async function loadStore(): Promise<ResourceTypeBindingsStore> {
    return await store.load();
  }

  async function saveOrganizationBindings(
    organizationId: string,
    bindings: ResourceTypeWorkspaceBinding[]
  ) {
    const current = await loadStore();
    await store.save({
      byOrganization: {
        ...current.byOrganization,
        [organizationId]: bindings
      }
    });
  }

  async function listOrganization(
    organizationId: string
  ): Promise<ResourceTypeWorkspaceBinding[]> {
    const current = await loadStore();
    return current.byOrganization[organizationId] ?? [];
  }

  async function upsertWithinWrite(
    organizationId: string,
    next: ResourceTypeWorkspaceBinding
  ): Promise<ResourceTypeWorkspaceBinding> {
    const bindings = await listOrganization(organizationId);
    const index = bindings.findIndex(
      binding =>
        binding.resourceTypeId === next.resourceTypeId &&
        binding.workspaceId === next.workspaceId
    );
    const updated =
      index === -1
        ? [...bindings, next]
        : bindings.map((binding, i) => (i === index ? next : binding));
    await saveOrganizationBindings(organizationId, updated);
    return next;
  }

  return {
    async list(organizationId) {
      return listOrganization(organizationId);
    },

    async setEnabled(input: SetResourceTypeEnabledInput) {
      return enqueueWrite(async () => {
        const current = resourceTypeBindingsUtils.resolveBinding(
          await listOrganization(input.organizationId),
          input.resourceTypeId,
          input.workspaceId
        );
        return upsertWithinWrite(input.organizationId, {
          ...current,
          enabled: input.enabled
        });
      });
    },

    async setWorkspaceFields(input: SetResourceTypeWorkspaceFieldsInput) {
      return enqueueWrite(async () => {
        const current = resourceTypeBindingsUtils.resolveBinding(
          await listOrganization(input.organizationId),
          input.resourceTypeId,
          input.workspaceId
        );
        return upsertWithinWrite(input.organizationId, {
          ...current,
          workspaceFields: input.workspaceFields
        });
      });
    }
  };
}
